import type { SavedTask } from './types'

// ─── 공유 그룹 ───
export interface SharedGroup {
  id: string                          // Firestore 문서 ID (auto)
  name: string
  description?: string
  color: string
  icon?: string
  thumbnailUrl?: string               // 그룹 대표 이미지

  // 소유권
  ownerId: string
  ownerName: string
  ownerPhotoURL?: string

  // 멤버 (보안 규칙용 + 빠른 조회)
  memberIds: string[]
  memberCount: number
  maxMembers: number                  // 기본값: 10

  // 초대 코드 (QR/링크 초대용)
  inviteCode: string                  // 8자리 랜덤 코드

  // 공지
  announcement?: string
  announcementSetBy?: string
  announcementSetByName?: string
  announcementSetAt?: string

  // 타임스탬프
  createdAt: string                   // ISO string
  updatedAt: string
  lastActivityAt: string

  // 채팅 리스트 미리보기용 (denormalized)
  lastChatText?: string
  lastChatSenderName?: string
  lastChatAt?: string
  lastChatType?: string
}

// ─── 멤버 (서브컬렉션 문서) ───
export type MemberRole = 'owner' | 'editor' | 'editor-limited' | 'viewer'

export interface SharedGroupMember {
  uid: string
  displayName: string
  email: string
  photoURL?: string
  role: MemberRole
  joinedAt: string
  lastSeenAt: string
  lastActiveAt?: string
  isOnline?: boolean
  isTyping?: boolean
  lastReadChatAt?: string
  isMuted?: boolean
  mutedUntil?: string
  status: 'active' | 'invited' | 'left'
}

// ─── 공유 할 일 (서브컬렉션 문서) ───
export interface SharedTask {
  id: string                          // Firestore 문서 ID
  title: string
  description?: string
  status: 'pending' | 'completed'
  priority: 'none' | 'low' | 'medium' | 'high'
  dueDate?: string                    // YYYY-MM-DD
  dueTime?: string

  // 협업 전용 (복수 담당자)
  assigneeIds?: string[]
  assigneeNames?: string[]
  // 하위 호환 (기존 단일 담당자)
  assigneeId?: string
  assigneeName?: string
  createdBy: string
  createdByName: string
  completedBy?: string
  completedByName?: string
  completedAt?: string

  // 리액션 { emoji: [uid, uid, ...] }
  reactions?: Record<string, string[]>

  // 라벨
  labels?: string[]

  // 고정
  isPinned?: boolean
  pinnedAt?: string
  pinnedBy?: string

  // 서브태스크 카운트
  subtaskCount?: number
  completedSubtaskCount?: number

  // 댓글 카운트
  commentCount?: number

  // 첨부파일
  attachments?: { name: string; url: string; size: number; type: string }[]

  sortOrder: number
  createdAt: string
  updatedAt: string
}

// ─── 서브태스크 ───
export interface TaskSubtask {
  id: string
  title: string
  isCompleted: boolean
  completedBy?: string
  completedByName?: string
  assigneeId?: string
  sortOrder: number
  createdAt: string
}

// ─── 댓글 ───
export interface TaskComment {
  id: string
  userId: string
  userName: string
  userPhotoURL?: string
  text: string
  mentions?: string[]                 // 멘션된 uid 배열
  parentId?: string                   // 답글 대상 (1단계만)
  imageUrl?: string                   // 인라인 이미지
  imageWidth?: number
  imageHeight?: number
  createdAt: string
  updatedAt?: string
}

// ─── 라벨 ───
export interface GroupLabel {
  id: string
  name: string
  color: string
  createdBy: string
  createdAt: string
}

// ─── 채팅 메시지 ───
export type ChatMessageType = 'text' | 'image' | 'video' | 'task_card' | 'memo_card' | 'system' | 'voice' | 'file'

export interface ChatMessage {
  id: string
  userId: string
  userName: string
  userPhotoURL?: string
  text: string
  type?: ChatMessageType              // 없으면 'text' fallback
  isDeleted?: boolean                  // 소프트 삭제 플래그
  mentions?: string[]
  taskRef?: string                    // 할일 링크 (taskId)

  // 이미지
  imageUrl?: string
  imageWidth?: number
  imageHeight?: number
  imageName?: string

  // 할일 카드 스냅샷
  taskSnapshot?: {
    title: string
    status: string
    priority: string
    assigneeName?: string
    dueDate?: string
  }

  // 메모 카드 스냅샷
  memoSnapshot?: {
    syncId: string
    title: string
    preview: string
    tags: string[]
    color: string
  }

  // 답장
  replyTo?: {
    messageId: string
    userId: string
    userName: string
    text: string
    imageUrl?: string
  }

  // 리액션
  reactions?: Record<string, string[]>

  // 음성 메시지
  voiceUrl?: string
  voiceDuration?: number              // 초 단위

  // 파일 첨부
  fileUrl?: string
  fileName?: string
  fileSize?: number
  fileType?: string                   // MIME type

  // 시스템 메시지
  systemEvent?: {
    action: string
    actorName?: string
    targetTitle?: string
  }

  // 링크 프리뷰 (OG 메타태그)
  linkPreview?: {
    url: string
    title?: string
    description?: string
    image?: string
    favicon?: string
  }

  createdAt: string
}

// 채팅 메시지 전송용 페이로드
export interface ChatMessagePayload {
  text: string
  type?: ChatMessageType
  mentions?: string[]
  taskRef?: string
  imageUrl?: string
  imageWidth?: number
  imageHeight?: number
  imageName?: string
  taskSnapshot?: ChatMessage['taskSnapshot']
  memoSnapshot?: ChatMessage['memoSnapshot']
  replyTo?: ChatMessage['replyTo']
  systemEvent?: ChatMessage['systemEvent']
  voiceUrl?: string
  voiceDuration?: number
  fileUrl?: string
  fileName?: string
  fileSize?: number
  fileType?: string
  linkPreview?: ChatMessage['linkPreview']
}

// ─── 공유 그룹 알림 ───
export type SharedNotificationType =
  | 'task_assigned' | 'task_completed' | 'task_commented'
  | 'task_mentioned' | 'task_reacted' | 'member_joined'
  | 'due_soon' | 'chat_mentioned'

export interface SharedNotification {
  id: string
  groupId: string
  groupName: string
  type: SharedNotificationType
  actorName: string
  actorPhotoURL?: string
  targetTitle?: string
  targetId?: string
  read: boolean
  createdAt: string
}

// ─── 초대 ───
export type InvitationStatus = 'pending' | 'accepted' | 'declined' | 'expired'

export interface GroupInvitation {
  id: string
  groupId: string
  groupName: string
  groupColor: string
  inviterId: string
  inviterName: string
  inviterPhotoURL?: string
  inviteeEmail: string
  inviteeId?: string
  role: 'editor' | 'viewer'
  status: InvitationStatus
  createdAt: string
  expiresAt: string                   // 7일 후 자동 만료
}

// ─── 그룹 파일 ───
export interface GroupFile {
  id: string
  name: string
  url: string
  size: number
  type: string                    // MIME type
  uploadedBy: string
  uploadedByName: string
  taskId?: string                 // 할일에 연결된 파일인 경우
  taskTitle?: string
  createdAt: string
}

// ─── 활동 로그 ───
export type ActivityAction =
  | 'task_created' | 'task_completed' | 'task_uncompleted'
  | 'task_updated' | 'task_deleted' | 'task_assigned'
  | 'task_commented' | 'task_reacted' | 'task_pinned' | 'task_unpinned'
  | 'subtask_created' | 'subtask_completed'
  | 'member_joined' | 'member_left' | 'member_role_changed' | 'member_mentioned'
  | 'group_updated' | 'chat_message'
  | 'file_uploaded' | 'file_deleted'

export interface GroupActivity {
  id: string
  userId: string
  userName: string
  userPhotoURL?: string
  action: ActivityAction
  targetId?: string
  targetTitle?: string
  metadata?: Record<string, string>
  createdAt: string
}

// ─── 통합 뷰용 래퍼 ───
export interface UnifiedTask {
  source: 'personal' | 'shared'
  personalTask?: SavedTask
  sharedTask?: SharedTask
  groupId?: string
  groupName?: string
  groupColor?: string

  // 통합 접근용 (공통 필드)
  id: string                          // personal: `p-${id}`, shared: `s-${groupId}-${taskId}`
  title: string
  status: 'pending' | 'completed'
  priority: 'none' | 'low' | 'medium' | 'high'
  dueDate?: string
  dueTime?: string
  assigneeName?: string
  assigneeNames?: string[]
}

// ─── 복수 담당자 정규화 유틸 ───
export function normalizeAssignees(
  task: Pick<SharedTask, 'assigneeId' | 'assigneeName' | 'assigneeIds' | 'assigneeNames'>
): { assigneeIds: string[]; assigneeNames: string[] } {
  if (task.assigneeIds && task.assigneeIds.length > 0) {
    return { assigneeIds: task.assigneeIds, assigneeNames: task.assigneeNames || [] }
  }
  if (task.assigneeId) {
    return { assigneeIds: [task.assigneeId], assigneeNames: task.assigneeName ? [task.assigneeName] : [] }
  }
  return { assigneeIds: [], assigneeNames: [] }
}
